import { useAuth } from "@/auth/hooks/useAuth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DASHBOARD_QUERY_GC_TIME,
  DASHBOARD_QUERY_STALE_TIMES,
  dashboardQueryKeys,
} from "@/dashboard/dashboardQueryConfig";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronDown, Clock } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

type AapProjectApiRow = {
  id: number;
  name: string;
  status: number;
  createdAt: string;
  approvalRequest: string;
  creator?: {
    id?: number;
    email?: string | null;
  } | null;
  program?: {
    appelAProjets?: {
      email?: string | null;
    } | null;
  } | null;
};

type ProjectTransformed = {
  id: number;
  pj: string;
  status: number;
  dateCreation: string;
  dureeAttente: string;
  email: string;
  _jours: number;
};

type ProjectGroups = {
  InProgress: ProjectTransformed[];
  WaitingValidation: ProjectTransformed[];
  WaitingValidationPP: ProjectTransformed[];
};

type WeekCountGroups = {
  InProgress: WeekCount;
  WaitingValidation: WeekCount;
  WaitingValidationPP: WeekCount;
};

type WeekCount = {
  less2: number;
  more2: number;
  more4: number;
};

const getDaysElapsed = (approvalRequest: string | Date | null | undefined) => {
  const approvalDate = new Date(approvalRequest);
  const today = new Date();

  // Calcul de la différence en millisecondes
  const diffInMs = today - approvalDate;

  // Convertir en jours
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  return diffInDays;
};
const ListingProject = () => {
  const { data, loading } = useFetchDataV2<AapProjectApiRow[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/AaP/projects`,
    dashboardQueryKeys.listingProjects,
    DASHBOARD_QUERY_STALE_TIMES.listingProjects,
    DASHBOARD_QUERY_GC_TIME,
  );
  const { user } = useAuth();
  const lang = localStorage.getItem("i18nextLng");
  const { t } = useTranslation("dashboard", {
    keyPrefix: "common",
  });
  const { t: tDashboard } = useTranslation("dashboard");
  const [projects, setProjects] = useState<ProjectGroups>({
    InProgress: [],
    WaitingValidation: [],
    WaitingValidationPP: [],
  });
  const [weekCount, setWeekCount] = useState<WeekCountGroups>({
    InProgress: {
      less2: 0,
      more2: 0,
      more4: 0,
    },
    WaitingValidation: {
      less2: 0,
      more2: 0,
      more4: 0,
    },
    WaitingValidationPP: {
      less2: 0,
      more2: 0,
      more4: 0,
    },
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const isContentLoading = loading || !user;

  useEffect(() => {
    const nextWeekCount: WeekCountGroups = {
      InProgress: {
        less2: 0,
        more2: 0,
        more4: 0,
      },
      WaitingValidation: {
        less2: 0,
        more2: 0,
        more4: 0,
      },
      WaitingValidationPP: {
        less2: 0,
        more2: 0,
        more4: 0,
      },
    };

    if (data && !loading) {
      const dataTransformed = data?.map((project) => {
        const sourceDate =
          project?.status == 1 ? project?.approvalRequest : project?.createdAt;
        const jours = getDaysElapsed(sourceDate);
        const sectionKey =
          project?.status == 1
            ? "InProgress"
            : project?.status == 2
              ? "WaitingValidation"
              : "WaitingValidationPP";

        if (jours > 13 && jours <= 27) {
          nextWeekCount[sectionKey].more2++;
        } else if (jours > 27) {
          nextWeekCount[sectionKey].more4++;
        } else {
          nextWeekCount[sectionKey].less2++;
        }

        return {
          id: project?.id,
          pj: project?.name,
          status: project?.status,
          dateCreation: new Date(sourceDate).toLocaleDateString(
            lang ? lang : "fr",
            {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }
          ),
          dureeAttente: `${getDaysElapsed(sourceDate)}`,
          _jours: jours,
          email:
            project?.creator == null || project?.creator?.id == user?.id
              ? t("Moi")
              : project?.program?.appelAProjets == null
              ? project?.creator?.email || ""
              : project?.program?.appelAProjets?.email || "",
        };
      });
      setWeekCount(nextWeekCount);

      setProjects({
        InProgress: dataTransformed
          ?.sort((a, b) => b._jours - a._jours)
          ?.filter((p) => p?.status == 1),
        WaitingValidation: dataTransformed
          ?.sort((a, b) => b._jours - a._jours)
          ?.filter((p) => p?.status == 2),
        WaitingValidationPP: dataTransformed
          ?.sort((a, b) => b._jours - a._jours)
          ?.filter((p) => p?.status == 6),
      });
    }
  }, [data, user, lang, loading, t]);

  return (
    <>
      <div className="bg-card rounded-[20px] my-5 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-green-900 text-[19px] mt-1 font-bold">
              {tDashboard("common.Suivi saisie et validation des projets")}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {tDashboard(
                "common.Suivez les projets en cours de saisie, en attente de validation et ceux qui nécessitent votre retour."
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsExpanded((current) => !current)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600"
          >
            {isExpanded
              ? tDashboard("common.Plier")
              : tDashboard("common.Déplier")}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-0" : "-rotate-90"}`}
            />
          </button>
        </div>
        {isExpanded ? (
          isContentLoading ? (
            <div className="mt-6">
              <Skeleton count={3} height={50} />
            </div>
          ) : (
            <Tabs className="mt-6 w-full" defaultValue="AwaitingValidationPP">
              <TabsList className="mb-2 grid w-full grid-cols-3 bg-background justify-center p-0 rounded-b-none">
                <TabsTrigger
                  className="rounded-none border-b-2 text-[#626262] data-[state=active]:border-foreground data-[state=active]:shadow-none h-full"
                  value="AwaitingValidationPP"
                >
                  {t("En attente de votre validation")}
                </TabsTrigger>
                <TabsTrigger
                  className="rounded-none border-b-2 text-[#626262] data-[state=active]:border-foreground data-[state=active]:shadow-none h-full"
                  value="AwaitingValidation"
                >
                  {t("En attente de validation Verdeo")}
                </TabsTrigger>
                <TabsTrigger
                  value="InProgress"
                  className="rounded-none border-b-2 text-[#626262] data-[state=active]:border-foreground data-[state=active]:shadow-none h-full"
                >
                  {t("En Cours")}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="AwaitingValidationPP" className="mb-4">
                <div className="m-5">
                  <Statistic
                    semaine0={weekCount.WaitingValidationPP.less2}
                    semaine1={weekCount.WaitingValidationPP.more2}
                    semaine2={weekCount.WaitingValidationPP.more4}
                  />
                </div>
                <div className="min-h-[150px]">
                  <TableProjet
                    data={projects?.WaitingValidationPP}
                    isAwaiting={true}
                    validationPP={true}
                  />
                </div>
              </TabsContent>
              <TabsContent value="AwaitingValidation" className="mb-4">
                <div className="m-5">
                  <Statistic
                    semaine0={weekCount.WaitingValidation.less2}
                    semaine1={weekCount.WaitingValidation.more2}
                    semaine2={weekCount.WaitingValidation.more4}
                  />
                </div>
                <div className="min-h-[150px]">
                  <TableProjet
                    data={projects?.WaitingValidation}
                    isAwaiting={true}
                    validationPP={false}
                  />
                </div>
              </TabsContent>
              <TabsContent value="InProgress" className="mb-4">
                <div className="m-5">
                  <Statistic
                    semaine0={weekCount.InProgress.less2}
                    semaine1={weekCount.InProgress.more2}
                    semaine2={weekCount.InProgress.more4}
                  />
                </div>
                <div className="min-h-[300px]">
                  <TableProjet
                    data={projects?.InProgress}
                    isAwaiting={false}
                    validationPP={false}
                  />
                </div>
              </TabsContent>
            </Tabs>
          )
        ) : null}
      </div>
    </>
  );
};
export default ListingProject;

interface StatisticProps {
  semaine0: number;
  semaine1: number;
  semaine2: number;
}
const Statistic: FC<StatisticProps> = ({ semaine0, semaine1, semaine2 }) => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "common",
  });
  return (
    <div className="mt-4">
      <div className="grid grid-cols-3 whitespace-nowrap text-left mt-2">
        {/* <span className="text-grey"> <b>Nombre total projets : {nbProjets} </b> </span> */}

        <span className="text-[#8d8d8db5]">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-[#8d8d8db5]"></div>
            <span className="text-sm text-[#8d8d8db5]-700">
              <b>
                {t("- de 2 semaines")} : {semaine0}
              </b>
            </span>
          </div>
        </span>
        <span className="text-[#ccb506b5] text-center">
          <div className="flex items-center gap-2 w-full">
            <div className="w-3 h-3 rounded-sm bg-[#ccb506b5]"></div>
            <span className="text-sm text-[#ccb506b5]-700 text-center">
              <b>
                {t("+ de 2 semaines")} : {semaine1}
              </b>
            </span>
          </div>
        </span>
        <span className="text-[#cc0606b5] text-right">
          <div className="flex items-center gap-2 w-full">
            <div className="w-3 h-3 rounded-sm bg-[#cc0606b5]"></div>
            <span className="text-sm text-[#cc0606b5]-700 text-right">
              <b>
                {t("+ de 4 semaines")} : {semaine2}
              </b>
            </span>
          </div>
        </span>
      </div>
    </div>
  );
};

interface TableProjetProps {
  data: ProjectTransformed[];
  isAwaiting: boolean;
  validationPP: boolean;
}

const TableProjet: FC<TableProjetProps> = ({
  data,
  isAwaiting,
  validationPP,
}) => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "common",
  });
  // Configuration des colonnes
  const columnHelper = createColumnHelper<ProjectTransformed>();
  const columns = [
    columnHelper.accessor("pj", {
      header: t("Projets"),
      cell: (info) => {
        const object = info.row.original;
        return (
          <a
            href={`/dashboard/project/${object.id}`}
            className="text-grey-600 hover:underline"
          >
            {info.getValue()}
          </a>
        );
      },
    }),
    columnHelper.accessor("email", {
      header: validationPP ? t("Intervenants") : t("Créateur"),
      cell: (info) => info.getValue(),
    }),

    columnHelper.accessor("dateCreation", {
      header: isAwaiting ? t("Demande d'approbation") : t("Date de création"),
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("dureeAttente", {
      header: isAwaiting ? t("Durée d'attente") : t("Depuis quand ?"),
      cell: (info) => {
        const object = info.row.original;
        const bgColor =
          object.dureeAttente > 27
            ? "bg-[#cc0606b5]"
            : object.dureeAttente > 13 && object.dureeAttente <= 27
            ? "bg-[#ccb506b5]"
            : "bg-[#8d8d8db5]";
        return (
          <div
            className={`${bgColor} inline-flex rounded-full px-3 py-1  items-center gap-1 whitespace-nowrap text-center`}
          >
            <span className="text-white text-[11px]">
              {info.getValue() == 0
                ? t("Aujourd'hui")
                : info.getValue() == 1
                ? t("Hier")
                : info.getValue() + t(" Jours")}{" "}
            </span>
            <Clock className="w-[15] h-5 text-white" />
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  return (
    <div className="mt-3">
      <div className="border border-gray-200 shadow-md rounded-lg overflow-hidden">
        {/* zone scrollable */}
        <div className="max-h-[45vh] overflow-y-auto">
          <table className="min-w-full table-fixed">
            <thead className="sticky top-0 z-10 bg-gray-100">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={`${
                        index === 0 || index === 1 ? "w-1/3" : "w-1/6"
                      } h-12 px-4 text-left text-sm font-semibold text-gray-700 border-b bg-white align-middle`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody className="bg-white">
              {data.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center py-4 text-gray-500 text-sm align-middle"
                  >
                    {t("Aucune donnée à afficher.")}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <td
                        key={cell.id}
                        className={`${
                          index === 0 || index === 1 ? "w-1/3" : "w-1/6"
                        } h-12 px-4 text-sm text-gray-800 border-b align-middle`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
